import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  FileText, 
  ShoppingCart, 
  Package, 
  Receipt, 
  Plus, 
  TrendingUp,
  TrendingDown,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Truck,
  DollarSign,
  Users,
  RotateCcw,
  CreditCard,
  FileQuestion,
  Shield,
  BarChart3,
  ArrowRight
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OTCDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    inquiries: { open: 0, converted: 0, total: 0 },
    quotations: { draft: 0, sent: 0, accepted: 0, converted: 0, total: 0, value: 0 },
    salesOrders: { open: 0, delivered: 0, completed: 0, total: 0, value: 0 },
    deliveries: { open: 0, issued: 0, total: 0 },
    billing: { draft: 0, released: 0, total: 0, value: 0 },
    returns: { open: 0, credited: 0, total: 0 },
    creditMemos: { posted: 0, total: 0, value: 0 },
    ar: { openItems: 0, overdueItems: 0, totalOpen: 0, totalOverdue: 0 }
  });
  const [recentDocs, setRecentDocs] = useState({
    inquiries: [],
    quotations: [],
    salesOrders: [],
    deliveries: [],
    billing: []
  });
  const [conversionStats, setConversionStats] = useState({
    inquiryToQuotation: 0,
    quotationToOrder: 0
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load all document counts in parallel
      const [
        inqRes, qtRes, soRes, delRes, billRes, retRes, cmRes, arRes, convRes
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/presales/inquiries`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/presales/quotations?include_expired=true`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/otc/sales-orders`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/otc/deliveries`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/otc/billing`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/otc/returns`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/otc/credit-memos`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/ar/open-items`).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/presales/statistics/conversion-rate`).catch(() => ({ data: {} }))
      ]);

      // Calculate Inquiry stats
      const inquiries = inqRes.data || [];
      const inqStats = {
        open: inquiries.filter(i => i.status === 'OPEN').length,
        converted: inquiries.filter(i => i.status === 'CONVERTED').length,
        total: inquiries.length
      };

      // Calculate Quotation stats
      const quotations = qtRes.data || [];
      const qtStats = {
        draft: quotations.filter(q => q.status === 'DRAFT').length,
        sent: quotations.filter(q => q.status === 'SENT').length,
        accepted: quotations.filter(q => q.status === 'ACCEPTED').length,
        converted: quotations.filter(q => q.status === 'CONVERTED').length,
        total: quotations.length,
        value: quotations.reduce((sum, q) => sum + (q.gross_value || 0), 0)
      };

      // Calculate Sales Order stats
      const salesOrders = soRes.data || [];
      const soStats = {
        open: salesOrders.filter(s => s.status === 'OPEN').length,
        delivered: salesOrders.filter(s => ['DELIVERED', 'PARTIALLY_DELIVERED'].includes(s.status)).length,
        completed: salesOrders.filter(s => s.status === 'COMPLETED').length,
        total: salesOrders.length,
        value: salesOrders.reduce((sum, s) => sum + (s.gross_value || 0), 0)
      };

      // Calculate Delivery stats
      const deliveries = delRes.data || [];
      const delStats = {
        open: deliveries.filter(d => d.status === 'OPEN').length,
        issued: deliveries.filter(d => d.status === 'GOODS_ISSUED').length,
        total: deliveries.length
      };

      // Calculate Billing stats
      const billing = billRes.data || [];
      const billStats = {
        draft: billing.filter(b => b.status === 'DRAFT').length,
        released: billing.filter(b => b.status === 'RELEASED').length,
        total: billing.length,
        value: billing.reduce((sum, b) => sum + (b.gross_value || 0), 0)
      };

      // Calculate Returns stats
      const returns = retRes.data || [];
      const retStats = {
        open: returns.filter(r => ['CREATED', 'RECEIVED', 'INSPECTED'].includes(r.status)).length,
        credited: returns.filter(r => r.status === 'CREDITED').length,
        total: returns.length
      };

      // Calculate Credit Memo stats
      const creditMemos = cmRes.data || [];
      const cmStats = {
        posted: creditMemos.filter(c => c.status === 'POSTED').length,
        total: creditMemos.length,
        value: creditMemos.reduce((sum, c) => sum + (c.gross_value || 0), 0)
      };

      // Calculate AR stats
      const openItems = arRes.data || [];
      const today = new Date();
      const overdueItems = openItems.filter(item => {
        if (item.status === 'CLEARED') return false;
        const dueDate = new Date(item.due_date);
        return dueDate < today;
      });
      const arStats = {
        openItems: openItems.filter(i => i.status === 'OPEN').length,
        overdueItems: overdueItems.length,
        totalOpen: openItems.filter(i => i.status === 'OPEN').reduce((sum, i) => sum + (i.open_amount || 0), 0),
        totalOverdue: overdueItems.reduce((sum, i) => sum + (i.open_amount || 0), 0)
      };

      setStats({
        inquiries: inqStats,
        quotations: qtStats,
        salesOrders: soStats,
        deliveries: delStats,
        billing: billStats,
        returns: retStats,
        creditMemos: cmStats,
        ar: arStats
      });

      // Set conversion stats
      const conv = convRes.data || {};
      setConversionStats({
        inquiryToQuotation: conv.inquiries?.conversion_rate || 0,
        quotationToOrder: conv.quotations?.conversion_rate || 0
      });

      // Get recent documents (last 5)
      setRecentDocs({
        inquiries: inquiries.slice(0, 5),
        quotations: quotations.slice(0, 5),
        salesOrders: salesOrders.slice(0, 5),
        deliveries: deliveries.slice(0, 5),
        billing: billing.slice(0, 5)
      });

    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'OPEN': 'bg-orange-100 text-orange-800',
      'DRAFT': 'bg-gray-100 text-gray-800',
      'SENT': 'bg-orange-100 text-orange-800',
      'ACCEPTED': 'bg-green-100 text-green-800',
      'CONVERTED': 'bg-emerald-100 text-emerald-800',
      'REJECTED': 'bg-red-100 text-red-800',
      'EXPIRED': 'bg-orange-100 text-orange-800',
      'DELIVERED': 'bg-green-100 text-green-800',
      'PARTIALLY_DELIVERED': 'bg-yellow-100 text-yellow-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'GOODS_ISSUED': 'bg-green-100 text-green-800',
      'RELEASED': 'bg-green-100 text-green-800',
      'CREDITED': 'bg-green-100 text-green-800',
      'POSTED': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const StatCard = ({ title, icon: Icon, color, stats, valueKey }) => (
    <div className={`${color} rounded-lg p-5 shadow-sm border`}>
      <div className="flex items-center justify-between mb-3">
        <div className={`p-2 rounded-lg bg-white bg-opacity-50`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold">{stats.total}</p>
          <p className="text-xs opacity-80">{title}</p>
        </div>
      </div>
      {valueKey && stats[valueKey] > 0 && (
        <div className="text-sm font-semibold mb-2">
          {formatCurrency(stats[valueKey])}
        </div>
      )}
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-opacity-20 border-current">
        {Object.entries(stats).map(([key, value]) => {
          if (key === 'total' || key === 'value') return null;
          return (
            <div key={key} className="text-center px-2">
              <p className="text-sm font-semibold">{value}</p>
              <p className="text-xs opacity-70 capitalize">{key.replace(/_/g, ' ')}</p>
            </div>
          );
        })}
      </div>
    </div>
  );

  const KPICard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
    <div className="bg-white rounded-lg p-4 shadow-sm border">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 rounded-full ${color.replace('text-', 'bg-').replace('600', '100')}`}>
          <Icon className={`w-6 h-6 ${color}`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center mt-2 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
          {Math.abs(trend)}% conversion rate
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-orange-600 mx-auto mb-4" />
            <p className="text-gray-600">Loading OTC Dashboard...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="otc-dashboard">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Order-to-Cash Dashboard</h1>
              <p className="text-gray-600 mt-1">Overview of all OTC transactions and KPIs</p>
            </div>
            <div className="flex gap-2">
              <Button 
                onClick={() => navigate('/presales/inquiries/create')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Inquiry
              </Button>
              <Button 
                onClick={() => navigate('/presales/quotations/create')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Quotation
              </Button>
              <Button 
                onClick={() => navigate('/otc/sales-orders/create')}
                className="bg-orange-600 hover:bg-orange-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Sales Order
              </Button>
            </div>
          </div>
        </div>

        {/* KPI Cards Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICard
            title="Total Sales Value"
            value={formatCurrency(stats.salesOrders.value)}
            subtitle={`${stats.salesOrders.total} orders`}
            icon={DollarSign}
            color="text-green-600"
          />
          <KPICard
            title="Billed Revenue"
            value={formatCurrency(stats.billing.value)}
            subtitle={`${stats.billing.total} invoices`}
            icon={Receipt}
            color="text-orange-600"
          />
          <KPICard
            title="Open Receivables"
            value={formatCurrency(stats.ar.totalOpen)}
            subtitle={`${stats.ar.openItems} open items`}
            icon={CreditCard}
            color="text-orange-600"
          />
          <KPICard
            title="Overdue Amount"
            value={formatCurrency(stats.ar.totalOverdue)}
            subtitle={`${stats.ar.overdueItems} overdue items`}
            icon={AlertCircle}
            color="text-red-600"
          />
        </div>

        {/* Conversion Rate Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Inquiry → Quotation</p>
                <p className="text-3xl font-bold text-orange-600">{conversionStats.inquiryToQuotation}%</p>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <FileQuestion className="w-6 h-6" />
                <ArrowRight className="w-4 h-4" />
                <FileText className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-3 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(conversionStats.inquiryToQuotation, 100)}%` }}
              />
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Quotation → Sales Order</p>
                <p className="text-3xl font-bold text-orange-600">{conversionStats.quotationToOrder}%</p>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <FileText className="w-6 h-6" />
                <ArrowRight className="w-4 h-4" />
                <ShoppingCart className="w-6 h-6" />
              </div>
            </div>
            <div className="mt-3 bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-600 h-2 rounded-full transition-all"
                style={{ width: `${Math.min(conversionStats.quotationToOrder, 100)}%` }}
              />
            </div>
          </Card>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Inquiries"
            icon={FileQuestion}
            color="bg-orange-50 text-orange-900"
            stats={stats.inquiries}
          />
          <StatCard
            title="Quotations"
            icon={FileText}
            color="bg-orange-50 text-orange-900"
            stats={stats.quotations}
            valueKey="value"
          />
          <StatCard
            title="Sales Orders"
            icon={ShoppingCart}
            color="bg-orange-50 text-orange-900"
            stats={stats.salesOrders}
            valueKey="value"
          />
          <StatCard
            title="Deliveries"
            icon={Truck}
            color="bg-orange-50 text-orange-900"
            stats={stats.deliveries}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            title="Billing"
            icon={Receipt}
            color="bg-green-50 text-green-900"
            stats={stats.billing}
            valueKey="value"
          />
          <StatCard
            title="Returns"
            icon={RotateCcw}
            color="bg-orange-50 text-orange-900"
            stats={stats.returns}
          />
          <StatCard
            title="Credit Memos"
            icon={CreditCard}
            color="bg-red-50 text-red-900"
            stats={stats.creditMemos}
            valueKey="value"
          />
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 text-white rounded-lg p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 rounded-lg bg-white bg-opacity-20">
                <Shield className="w-5 h-5" />
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold">{stats.ar.openItems}</p>
                <p className="text-xs opacity-80">AR Open Items</p>
              </div>
            </div>
            <div className="flex justify-between mt-3 pt-3 border-t border-white border-opacity-20">
              <div className="text-center">
                <p className="text-sm font-semibold text-green-400">{formatCurrency(stats.ar.totalOpen)}</p>
                <p className="text-xs opacity-70">Open</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-red-400">{formatCurrency(stats.ar.totalOverdue)}</p>
                <p className="text-xs opacity-70">Overdue</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-sm border p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Quick Actions
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-5 lg:grid-cols-6 gap-4">
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-orange-200 hover:bg-orange-50"
              onClick={() => navigate('/presales/inquiries')}
            >
              <FileQuestion className="w-5 h-5 text-orange-600" />
              <span className="text-xs">Inquiries</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-orange-200 hover:bg-orange-50"
              onClick={() => navigate('/presales/quotations')}
            >
              <FileText className="w-5 h-5 text-orange-600" />
              <span className="text-xs">Quotations</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-orange-200 hover:bg-orange-50"
              onClick={() => navigate('/otc/sales-orders')}
            >
              <ShoppingCart className="w-5 h-5 text-orange-600" />
              <span className="text-xs">Sales Orders</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-orange-200 hover:bg-orange-50"
              onClick={() => navigate('/otc/deliveries')}
            >
              <Truck className="w-5 h-5 text-orange-600" />
              <span className="text-xs">Deliveries</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-green-200 hover:bg-green-50"
              onClick={() => navigate('/otc/billing')}
            >
              <Receipt className="w-5 h-5 text-green-600" />
              <span className="text-xs">Billing</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-orange-200 hover:bg-orange-50"
              onClick={() => navigate('/otc/returns')}
            >
              <RotateCcw className="w-5 h-5 text-orange-600" />
              <span className="text-xs">Returns</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-red-200 hover:bg-red-50"
              onClick={() => navigate('/otc/credit-memos')}
            >
              <CreditCard className="w-5 h-5 text-red-600" />
              <span className="text-xs">Credit Memos</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-orange-200 hover:bg-orange-50"
              onClick={() => navigate('/ar/open-items')}
            >
              <DollarSign className="w-5 h-5 text-orange-600" />
              <span className="text-xs">AR Open Items</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-emerald-200 hover:bg-emerald-50"
              onClick={() => navigate('/ar/payments')}
            >
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              <span className="text-xs">Payments</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-pink-200 hover:bg-pink-50"
              onClick={() => navigate('/ar/aging-report')}
            >
              <BarChart3 className="w-5 h-5 text-pink-600" />
              <span className="text-xs">AR Aging</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-yellow-200 hover:bg-yellow-50"
              onClick={() => navigate('/otc/credit-exposure')}
            >
              <Shield className="w-5 h-5 text-yellow-600" />
              <span className="text-xs">Credit Exposure</span>
            </Button>
            <Button
              variant="outline"
              className="h-20 flex flex-col items-center justify-center gap-2 border-gray-200 hover:bg-gray-50"
              onClick={() => navigate('/otc/atp-check')}
            >
              <Package className="w-5 h-5 text-gray-600" />
              <span className="text-xs">ATP Check</span>
            </Button>
          </div>
        </div>

        {/* Recent Documents */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recent Quotations */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <FileText className="w-5 h-5 text-orange-600" />
                Recent Quotations
              </h3>
            </div>
            <div className="divide-y">
              {recentDocs.quotations.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No quotations yet</p>
                </div>
              ) : (
                recentDocs.quotations.map((qt) => (
                  <div
                    key={qt.quotation_number}
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/presales/quotations/${qt.quotation_number}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-orange-600">{qt.quotation_number}</p>
                        <p className="text-sm text-gray-600">
                          {qt.customer_id} • {formatCurrency(qt.gross_value || 0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Valid: {qt.valid_from} - {qt.valid_to}
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(qt.status)}`}>
                        {qt.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Recent Sales Orders */}
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-4 border-b">
              <h3 className="font-semibold flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-orange-600" />
                Recent Sales Orders
              </h3>
            </div>
            <div className="divide-y">
              {recentDocs.salesOrders.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-30" />
                  <p>No sales orders yet</p>
                </div>
              ) : (
                recentDocs.salesOrders.map((so) => (
                  <div
                    key={so.sales_order_number}
                    className="p-4 hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/otc/sales-orders/${so.sales_order_number}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-orange-600">{so.sales_order_number}</p>
                        <p className="text-sm text-gray-600">
                          {so.customer_id} • {formatCurrency(so.gross_value || 0)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {so.items?.length || 0} items
                        </p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded ${getStatusColor(so.status)}`}>
                        {so.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default OTCDashboard;
